import { Component, ErrorInfo, ReactNode } from 'react';
import { isChunkLoadError, reloadOnce } from '../lib/chunkReload';

interface Props {
    children?: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

// Este boundary es la ultima linea de defensa antes de que el usuario vea una pantalla rota, asi
// que se mantiene deliberadamente simple: no depende del contexto de i18next (podria no estar
// listo si el error ocurrio muy temprano), sino que lee el idioma guardado directamente, igual
// que el arranque de la app en src/i18n/index.ts.
const isSpanish = () => {
    try {
        return localStorage.getItem('preferredLanguage') === 'es';
    } catch {
        return false;
    }
};

export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
        error: null
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('Uncaught error:', error, errorInfo);

        // Si el error es un chunk de Vite obsoleto (version vieja pidiendo un archivo que ya no
        // existe tras un deploy), intentamos recuperarnos solos con una recarga silenciosa antes
        // de mostrarle nada tecnico al usuario.
        if (isChunkLoadError(error)) {
            reloadOnce();
        }
    }

    private handleReload = () => {
        window.location.href = '/';
    };

    public render() {
        if (this.state.hasError) {
            const es = isSpanish();

            return (
                <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 px-4">
                    <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
                        <div className="text-5xl mb-4">😕</div>
                        <h1 className="text-xl font-bold text-gray-900 mb-2">
                            {es ? '¡Ups! Algo salió mal' : 'Oops! Something went wrong'}
                        </h1>
                        <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                            {es
                                ? 'No pudimos cargar esta página. Puede que hayamos actualizado la app mientras la tenías abierta. Intenta recargar.'
                                : "We couldn't load this page. We may have updated the app while you had it open. Please try reloading."}
                        </p>
                        <button
                            onClick={this.handleReload}
                            className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-xl hover:shadow-lg transition-all cursor-pointer"
                        >
                            {es ? 'Volver al inicio' : 'Back to home'}
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
